'use client';

import { Link } from '@larvitu/shared/next/mui';
import { useOffSetTop } from '@larvitu/shared/react/hooks';
import { Box, Divider, Stack } from '@mui/material';

import { HEADER_DESKTOP_HEIGHT } from './config';
import { Header } from './Header';

import type { SxProps } from '@mui/material';
import type { LinkProps } from 'next/link';
import type { Route } from 'nextjs-routes';
import type { Component, ComponentType, FC } from 'react';

export type RouteSection = Record<string, Route>;

export type Routes = Record<string, RouteSection | Route>;

type NavConfig = Record<string, unknown>;

export interface NavProps {
  isScrolling?: boolean | undefined;
  isTransparent?: boolean | undefined;
  navConfig: NavConfig;
  sx?: SxProps;
}

export interface StandardHeaderProps {
  transparent?: boolean;
  routes?: Routes;
  brandHref?: LinkProps['href'];
  Logo?: ComponentType<{ isSimple: boolean; onDark: boolean }>;
  Label?: ComponentType;
  navDesktop?: ComponentType<{
    isScrolling: boolean;
    isTransparent: boolean;
    navConfig: NavConfig;
  }>;
  navMobile?: ComponentType<{ navConfig: NavConfig } & NavProps>;
  Searchbar?: typeof Component;
  LanguagePopover?: ComponentType;
  navConfig: NavConfig;
}

// -----------------------------------------------------------------------------

// commma works as "or"
// const isDesktopMediaQuery = '@media (min-width: 900px) , (hover: hover)';
const isMobileMediaQuery = '@media (max-width: 900px) , (hover: none)';

// todo: replace with a more abstracted implementation
/** concrete Implementation of Header */
export const StandardHeader: FC<StandardHeaderProps> = ({
  brandHref = '/',
  transparent = false,
  navDesktop: NavDesktop,
  navMobile: NavMobile,
  Logo,
  Label,
  Searchbar,
  LanguagePopover,
  navConfig,
}) => {
  const isScrolling = useOffSetTop( HEADER_DESKTOP_HEIGHT );

  return (
    <>
      <Header transparent={transparent} isScrolling={isScrolling}>
        <>
          <Link noLinkStyle href={brandHref}>
            <Box
              sx={{
                lineHeight: 0,
                position: 'relative',
                display: 'flex',
              }}
            >
              {Logo && (
                <Logo
                  isSimple={isScrolling}
                  onDark={transparent && !isScrolling}
                />
              )}
              {Label && <Label />}
            </Box>
          </Link>

          <Box
            sx={{
              display: 'block',
              [isMobileMediaQuery]: {
                display: 'none',
              },
            }}
          >
            <NavDesktop
              isScrolling={isScrolling}
              isTransparent={transparent}
              navConfig={navConfig}
            />
          </Box>

          {(Searchbar || LanguagePopover) && (
            <>
              <Box sx={{ flexGrow: 1 }} />

              <Stack spacing={2} direction="row" alignItems="center">
                {Searchbar && (
                  <Searchbar
                    sx={{
                      ...(isScrolling && { color: 'text.primary' }),
                    }}
                  />
                )}

                {LanguagePopover && (
                  <LanguagePopover
                    sx={{
                      ...(isScrolling && { color: 'text.primary' }),
                    }}
                  />
                )}

                <Divider orientation="vertical" sx={{ height: 24 }} />
              </Stack>
            </>
          )}
          <Box
            sx={{
              display: 'none',
              [isMobileMediaQuery]: {
                display: 'block',
              },
            }}
          >
            <NavMobile
              navConfig={navConfig}
              sx={{
                ml: 1,
                ...(isScrolling && { color: 'text.primary' }),
              }}
            />
          </Box>
        </>
      </Header>
      {/*<HeaderHeightPadding />*/}
    </>
  );
};
